import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from 'src/app/constants/templates.constants';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { DialogService, CoreService, SpinnerService, SaveFormEvent, CardSetTransformationService, DialogResult } from 'dspx-core';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { POP_UP_CODES, SAVE_RESULT_STATUS } from 'src/app/constants/app-data.constants';
import { finalize, take } from 'rxjs';
import { CareAllowanceLocalAdoptionSaveRequest } from 'src/app/interfaces/care-allowance-local-adoption-request.interface';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { getDateFromDDMMYYYYString } from 'src/app/helpers/date-helper';
import { ValidationResultCode } from 'src/app/enum/validation-result-code.enum';

@Component({
  selector: 'app-care-allowance-local-adoption',
  templateUrl: './care-allowance-local-adoption.component.html',
  styleUrl: './care-allowance-local-adoption.component.scss'
})
export class CareAllowanceLocalAdoptionComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.careAllowanceLocalAdoption;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  personId: string = '';
  roleId: string = '';
  associatedPersonId: string = '';
  isFromClientContact: boolean;
  localAdoptionId: string = '';
  isReadOnly: boolean;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private errorService: ErrorService,
    private spinnerService: SpinnerService,
    protected responseParserService: ResponseParserService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    const routeMappingSubscription = this.route.paramMap.subscribe((paramMap: ParamMap) => {
      this.personId = paramMap.get('personId');
      this.roleId = paramMap.get('roleId');
      this.localAdoptionId = paramMap.get('id');
      if (this.personId && this.roleId) {
        this.id = `${this.personId}/${this.roleId}`;
        this.associatedPersonId = paramMap.get('associatedPersonId');
        if (this.associatedPersonId) {
          this.id = `${this.id}/${this.associatedPersonId}`;
        }
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getNewCareAllowanceLocalAdoptionConfigAndResponse;
      }
      else if (this.localAdoptionId) {
        this.id = this.localAdoptionId;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceLocalAdoptionConfigAndResponse;
        this.isReadOnly = true;
      }
    });
    this.registerSubscription(
      routeMappingSubscription
    );

    this.coreService.conditionFunctions = {
      showClientType: () => this.isFromClientContact,
      showCarerType: () => !this.isFromClientContact,
      isReadonly: () => this.isReadOnly,
    };

    this.coreService.applicationFunctions = {
      matchClientContact: () => this.matchContact(),
      matchCarerContact: () => this.matchContact(),
      showMatchClientContactButton: () => !this.isFromClientContact && !this.isReadOnly,
      showMatchCarerContactButton: () => this.isFromClientContact && !this.isReadOnly,
      showSaveButton: () => !this.isReadOnly,
      cancel: () => this.navigateBack(),
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
    if (this.baseForm && this.baseForm.model) {
      this.isFromClientContact = event.isFromClientContact;
    }
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  cancel(event: any) {
    this.navigateBack();
  }

  save(event: SaveFormEvent) {
    if (!event.isFormValid) {
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      return;
    }
    this.activeDialog = '';
    this.activeDialog = POP_UP_CODES.localAdoption.createPlacement;
    this.dialogService.showConfirm(this.activeDialog)
      .pipe(take(1))
      .subscribe(data => {
        if (data === DialogResult.ACCEPTED) {
          this.spinnerService.showSpinner();
          const clientId = this.isFromClientContact ? this.personId : this.associatedPersonId;
          const carerId = !this.isFromClientContact ? this.personId : this.associatedPersonId;
          if (!clientId) {
            this.spinnerService.hideSpinner();
            this.processErrorMessage(ValidationResultCode.NoClient);
            return;
          }
          if (!carerId) {
            this.spinnerService.hideSpinner();
            this.processErrorMessage(ValidationResultCode.NoCarer);
            return;
          }
          const parsedJson = JSON.parse(event.model);
          const page2 = parsedJson.form.tabs.tab1.page2;
          const serviceProviderIdAndNameId = page2.serviceProviderDetailsSection.serviceProviderIdAndName;
          const levelId = page2.placementDetailsSection.level;
          const startDate = getDateFromDDMMYYYYString(page2.placementDetailsSection.placementStartDate);
          const ceaseDateValue = page2.placementDetailsSection.placementCeaseDate;
          const ceaseDate = ceaseDateValue ? getDateFromDDMMYYYYString(ceaseDateValue,) : null;
          const request: CareAllowanceLocalAdoptionSaveRequest =
          {
            clientId,
            carerId,
            levelId: levelId,
            localAdoptionServiceProviderId: serviceProviderIdAndNameId,
            startDate: startDate,
            ceaseDate: ceaseDate,
          };
          this.registerSubscription(
            this.appDataService
              .post(API_ENDPOINTS.careAllowancePlacement.saveLocalAdoption, request)
              .pipe(
                take(1),
                this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner())
              )
              .subscribe(response => {
                if (response?.status === SAVE_RESULT_STATUS.success) {
                  const errorMessageCode = response.message;
                  if (errorMessageCode) {
                    this.processErrorMessage(errorMessageCode)
                    return;
                  }
                  this.navigate(['/', ROUTE_CONSTANTS.careAllowanceLocalAdoption.prefix, response.id], true);
                }
              })
          );
        }
      });
  }

  private processErrorMessage(errorMessageCode: string) {
    this.activeDialog = '';
    switch (errorMessageCode) {
      case ValidationResultCode.NoClient:
        this.activeDialog = POP_UP_CODES.localAdoption.noClient;
        break;
      case ValidationResultCode.NoCarer:
        this.activeDialog = POP_UP_CODES.localAdoption.noCarer;
        break;
      case ValidationResultCode.InactiveContact:
        this.activeDialog = POP_UP_CODES.localAdoption.inactiveContact;
        break;
      case ValidationResultCode.ContactNotAVendor:
        this.activeDialog = POP_UP_CODES.localAdoption.contactNotAVendor;
        break;
      case ValidationResultCode.PlacementAttributeCombination:
        this.activeDialog = POP_UP_CODES.localAdoption.placementAttributeCombination;
        break;
      case ValidationResultCode.SameCarerSameClientOverlappingPlacement:
        this.activeDialog = POP_UP_CODES.localAdoption.sameCarerSameClientOverlappingPlacement;
        break;
      case ValidationResultCode.SameClientDifferentCarerOverlappingPlacement:
        this.activeDialog = POP_UP_CODES.localAdoption.sameClientDifferentCarerOverlappingPlacement;
        break;
      default:
        break;
    }
    if (this.activeDialog) {
      this.dialogService.showDialog(this.activeDialog);
    }
  }

  private matchContact() {
    this.router.navigate(['/', ROUTE_CONSTANTS.person.list,
      ROUTE_CONSTANTS.person.updateCreateLocalAdoptionPlacementPrefix,
      ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.person,
      this.personId,
      ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.role,
      this.roleId,
      ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.isFromClient,
      this.isFromClientContact,
    ],);
  }

  private navigateBack() {
    if (this.personId) {
      this.router.navigate(['/', ROUTE_CONSTANTS.person.prefixDetail, this.personId]);
    }
    else {
      this.router.navigate(['/', ROUTE_CONSTANTS.person.list]);
    }
  }
}
